import { useEffect, useState, type MouseEvent } from 'react'
import type { FindingsAttentionItem, FindingsAttentionScope } from '../../lib/api.ts'
import { formatCount } from '../../lib/findingsReport.ts'
import { totalPages } from '../../lib/pagination.ts'
import { toPath, type Route } from '../../lib/route.ts'
import { STATUS_LABEL } from '../../lib/vocabulary.ts'
import { useFindingsAttention } from '../../lib/useFindingsAttention.ts'
import { Button } from '../Button.tsx'
import { Pagination } from '../Pagination.tsx'
import { TD, TH } from '../DataTable.tsx'

export function FindingsAttentionModal({
  scope,
  onOpen,
  onClose,
}: {
  scope: FindingsAttentionScope
  onOpen: (item: FindingsAttentionItem, event: MouseEvent<HTMLAnchorElement>) => void
  onClose: () => void
}) {
  const [page, setPage] = useState(1)
  const { answered, loading, error } = useFindingsAttention(scope, page)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Conferências com apontamentos sem resposta"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="rounded-base bg-background-raised border-stroke-weak flex max-h-[90vh] w-full max-w-[72rem] flex-col gap-3 overflow-y-auto border p-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-tPSemi text-text-strong">Apontamentos sem resposta</h2>
            {answered !== undefined && (
              <p className="text-tSmall text-text-weak">
                {formatCount(answered.total)} conferências neste recorte
              </p>
            )}
          </div>
          <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
            Fechar
          </Button>
        </div>
        <p className="text-tSmall text-text-weak max-w-prose">
          Conferências concluídas ou enviadas à produção que ainda têm apontamentos sem resposta.
        </p>
        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
        {answered === undefined ? (
          error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
        ) : answered.items.length === 0 ? (
          <p className="text-tP text-text-weak">Nenhuma conferência neste recorte.</p>
        ) : (
          <div className={loading ? 'opacity-60' : ''}>
            <div className="w-full overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {[
                      'Conta',
                      'Conferência',
                      'Situação',
                      'Apontamentos',
                      'Respondidos',
                      'Sem resposta',
                    ].map((heading) => (
                      <th key={heading} scope="col" className={TH}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {answered.items.map((item) => {
                    const route: Route = {
                      name: 'verification',
                      at: { kind: 'identified', id: item.id },
                    }
                    return (
                      <tr key={item.id}>
                        <td className={TD}>{item.accountName}</td>
                        <td className={`${TD} whitespace-nowrap`}>
                          <a
                            href={toPath(route)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-text-brand font-semibold underline underline-offset-2"
                            onClick={(event) => onOpen(item, event)}
                          >
                            Filial {item.branchCode} · Orçamento {item.quotationNumber}
                          </a>
                        </td>
                        <td className={TD}>{STATUS_LABEL[item.status] ?? item.status}</td>
                        <td className={`${TD} tabular-nums`}>{formatCount(item.flagged)}</td>
                        <td className={`${TD} tabular-nums`}>{formatCount(item.responded)}</td>
                        <td className={`${TD} tabular-nums`}>{formatCount(item.unanswered)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {answered !== undefined && (
          <Pagination
            currentPage={page}
            totalPages={totalPages(answered.total, answered.pageSize)}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}
